"use client";
import { useEffect, useRef, useState } from "react";
import type { TestimonialsSection } from "./types";
import type { SiteLocale } from "./site-languages";
import { safeHref } from "./design";

function GoogleMark() {
  return <span className="carousel-google-wordmark" translate="no" aria-label="Google"><span>G</span><span>o</span><span>o</span><span>g</span><span>l</span><span>e</span></span>;
}

export function ReviewCarousel({ items, locale, provider, googleMapsUrl }: { items: TestimonialsSection["items"]; locale: SiteLocale; provider?: TestimonialsSection["provider"]; googleMapsUrl?: string }) {
  const viewport = useRef<HTMLDivElement>(null), group = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false), [hovered, setHovered] = useState(false), [focused, setFocused] = useState(false);
  const [reduced, setReduced] = useState(true);
  const el = locale === "el";
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update(); media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const stopped = paused || hovered || focused || reduced || items.length < 2;
  useEffect(() => {
    if (stopped) return;
    let frame = 0, previous = 0, position = viewport.current?.scrollLeft || 0;
    const tick = (now: number) => {
      const node = viewport.current, width = group.current?.offsetWidth || 0;
      if (node && width) {
        if (previous) position += Math.min(now - previous, 50) * 0.018;
        if (position >= width) position -= width;
        node.scrollLeft = position;
      }
      previous = now; frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [stopped, items.length]);
  function move(direction: number) {
    setPaused(true);
    const node = viewport.current, width = group.current?.offsetWidth || 0;
    if (!node || !width) return;
    const step = (group.current?.firstElementChild as HTMLElement | null)?.offsetWidth || 340;
    let next = node.scrollLeft + direction * (step + 20);
    if (next < 0) { node.scrollLeft += width; next += width; }
    node.scrollTo({ left: next % width, behavior: reduced ? "instant" : "smooth" });
  }
  const cards = (duplicate = false) => items.map(item => <blockquote className="review-carousel-card" key={item.id}>
    {provider === "google" && <div className="carousel-google-card-heading"><GoogleMark/><span>{el ? "Κριτική Google" : "Google review"}</span></div>}
    {provider === "google" && item.rating && <div className="carousel-google-stars" role="img" aria-label={`${item.rating} / 5`}><span aria-hidden="true">{"★".repeat(item.rating)}{"☆".repeat(5 - item.rating)}</span></div>}
    <p>{item.quote}</p><footer><strong>{item.name}</strong>{item.role && <small>{item.role}</small>}
      {item.sourceUrl && <a href={safeHref(item.sourceUrl)} target="_blank" rel="noopener noreferrer" tabIndex={duplicate ? -1 : undefined}>{provider === "google" && item.sourceUrl.startsWith("https://www.google.com/maps/") ? (el ? "Δείτε στο Google" : "Read on Google") : (el ? "Πηγή κριτικής" : "Review source")} ↗</a>}
    </footer>
  </blockquote>);
  return <div className="review-carousel" role="region" aria-label={el ? "Κριτικές πελατών" : "Customer reviews"}
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
    {provider === "google" && <div className="carousel-google-attribution"><div><GoogleMark/><span>{el ? "Κριτικές πελατών στο Google" : "Customer reviews on Google"}</span></div>{googleMapsUrl && safeHref(googleMapsUrl) !== "#" && <a href={safeHref(googleMapsUrl)} target="_blank" rel="noopener noreferrer">{el ? "Δείτε τις κριτικές στο Google" : "See reviews on Google"} ↗</a>}</div>}
    <div className="review-carousel-controls">
      <button type="button" onClick={() => move(-1)} aria-label={el ? "Προηγούμενες κριτικές" : "Previous reviews"}>←</button>
      <button type="button" aria-pressed={paused || reduced} disabled={reduced} onClick={() => setPaused(value => !value)}>{reduced ? (el ? "Χωρίς κίνηση" : "Reduced motion") : paused ? (el ? "Συνέχεια" : "Play reviews") : (el ? "Παύση" : "Pause reviews")}</button>
      <button type="button" onClick={() => move(1)} aria-label={el ? "Επόμενες κριτικές" : "Next reviews"}>→</button>
    </div>
    <div ref={viewport} className="review-carousel-viewport" tabIndex={0} data-paused={stopped} onPointerDown={() => setPaused(true)} aria-label={el ? "Οριζόντια λίστα κριτικών" : "Scrollable reviews"}>
      <div className="review-carousel-track"><div ref={group} className="review-carousel-group">{cards()}</div>{items.length > 1 && <div className="review-carousel-group" aria-hidden="true" inert>{cards(true)}</div>}</div>
    </div>
  </div>;
}
